import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star, X } from "lucide-react";
import {
	fetchWPSeoHealth,
	type SeoPageEntry,
} from "@/services/wpSeoHealthService";
import { technicalAuditService } from "@/services/technicalAuditService";
import { useTenantStore } from "@/stores/useTenantStore";
import { Badge } from "@/components/ui/badge";
import { SectionPanel } from "@/components/dashboard/SectionPanel";
import { IntegrationStatePanel } from "@/components/dashboard/IntegrationStatePanel";
import { SeoHealthTable } from "@/components/dashboard/tables/SeoHealthTable";
import { SchemaTypesChips } from "@/components/dashboard/tables/SchemaTypesChips";
import { TechnicalAuditRollup } from "@/components/dashboard/tables/TechnicalAuditRollup";

const ISSUE_FILTERS = {
	poor_score: {
		label: "Poor SEO score",
		matches: (p: SeoPageEntry) => p.seo_score < 41,
	},
	missing_meta: {
		label: "Missing meta desc",
		matches: (p: SeoPageEntry) => !p.meta_description,
	},
	no_schema: {
		label: "No schema",
		matches: (p: SeoPageEntry) => (p.schema_pro_schemas ?? []).length === 0,
	},
} as const;

type IssueFilter = keyof typeof ISSUE_FILTERS;

function parseIssueFilter(raw: string | null): IssueFilter | null {
	return raw === "poor_score" || raw === "missing_meta" || raw === "no_schema" ? raw : null;
}

function getApiErrorMessage(error: unknown, fallback: string) {
	return (
		(error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
		(error as Error)?.message ||
		fallback
	);
}

export default function SiteHealthPage() {
	const { currentTenant } = useTenantStore();
	const tenantScope = currentTenant ?? "no-tenant";
	const [searchParams, setSearchParams] = useSearchParams();
	const issueFilter = parseIssueFilter(searchParams.get("issue"));

	const clearIssueFilter = () => {
		const next = new URLSearchParams(searchParams);
		next.delete("issue");
		setSearchParams(next, { replace: true });
	};

	// Technical audit rollup (doc 15 §8 lead content) and the WP SEO inventory
	// are independent panels: a failure in one never blanks the other.
	const {
		data: taRuns,
		isLoading: runsLoading,
		isError: runsError,
		error: runsErrorObj,
		refetch: runsRefetch,
	} = useQuery({
		queryKey: ["technical-audit", tenantScope, "runs"],
		queryFn: () => technicalAuditService.listRuns(currentTenant ?? ""),
		staleTime: 10 * 60 * 1000,
		enabled: !!currentTenant,
		retry: false,
	});

	// Runs arrive newest-first (backend orders by created_at desc); the rollup
	// reflects the latest COMPLETED run only — in-flight/failed runs show as
	// "no audit yet" rather than a stale or partial rollup.
	const latestCompletedRun = taRuns?.runs.find((r) => r.status === "completed");

	const {
		data: taIssues,
		isLoading: summaryLoading,
		isError: summaryError,
		error: summaryErrorObj,
		refetch: summaryRefetch,
	} = useQuery({
		queryKey: ["technical-audit", tenantScope, "issue-summary", latestCompletedRun?.id ?? ""],
		// pageSize 1: only the server-computed summary (by_type/by_severity) is
		// consumed here; the full issue list lives on /audit/technical-issues.
		queryFn: () => technicalAuditService.getIssues(latestCompletedRun?.id ?? "", { page: 1, pageSize: 1 }),
		staleTime: 10 * 60 * 1000,
		enabled: !!latestCompletedRun,
		retry: false,
	});

	const {
		data: seo,
		isLoading: wpLoading,
		isError: wpError,
		error: wpErrorObj,
		refetch: wpRefetch,
	} = useQuery({
		queryKey: ["wp-seo-health", tenantScope],
		queryFn: () => fetchWPSeoHealth(currentTenant),
		staleTime: 10 * 60 * 1000,
		enabled: !!currentTenant,
		retry: false,
	});

	const seoAgg = seo?.aggregate;
	const seoPages = seo?.pages ?? [];
	const filteredPages = issueFilter ? seoPages.filter(ISSUE_FILTERS[issueFilter].matches) : seoPages;

	const taLoading = runsLoading || (!!latestCompletedRun && summaryLoading);

	return (
		<div className="w-full space-y-6">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<h1 className="text-dash-page font-semibold text-foreground">Site health</h1>
				{issueFilter && (
					<Badge variant="secondary" className="inline-flex items-center gap-1">
						Filter: {ISSUE_FILTERS[issueFilter].label}
						<button
							type="button"
							aria-label="Clear filter"
							onClick={clearIssueFilter}
							className="ml-0.5 inline-flex rounded-full hover:text-foreground"
						>
							<X className="h-3 w-3" />
						</button>
					</Badge>
				)}
			</div>
			<SectionPanel
				title="Technical audit"
				caption={latestCompletedRun?.completed_at ? `Site snapshot · crawl ${latestCompletedRun.completed_at.slice(0, 10)}` : "Site snapshot"}
				surfaceEmphasis="primary"
			>
				{taLoading ? (
					<IntegrationStatePanel state="loading" skeleton="rows" />
				) : runsError ? (
					<IntegrationStatePanel state="error" errorReason={getApiErrorMessage(runsErrorObj, "Couldn't load technical audit runs")} onRetry={() => runsRefetch()} />
				) : summaryError ? (
					<IntegrationStatePanel state="error" errorReason={getApiErrorMessage(summaryErrorObj, "Couldn't load technical audit issues")} onRetry={() => summaryRefetch()} />
				) : !latestCompletedRun ? (
					<IntegrationStatePanel state="empty" message="No technical audit yet — run a crawl from the audit tools" />
				) : !taIssues ? null : (
					<TechnicalAuditRollup
						runId={latestCompletedRun.id}
						bySeverity={taIssues.summary.by_severity}
						byType={taIssues.summary.by_type}
					/>
				)}
			</SectionPanel>
			{wpLoading ? (
				<SectionPanel title="WordPress SEO">
					<IntegrationStatePanel state="loading" skeleton="rows" />
				</SectionPanel>
			) : wpError ? (
				<SectionPanel title="WordPress SEO">
					<IntegrationStatePanel state="error" errorReason={getApiErrorMessage(wpErrorObj, "Couldn't load WordPress SEO data")} onRetry={() => wpRefetch()} />
				</SectionPanel>
			) : !seoAgg ? (
				<SectionPanel title="WordPress SEO">
					<IntegrationStatePanel
						state="not_connected"
						headline="WordPress SEO isn't connected yet."
						message="No WordPress SEO data — make sure the AgileCO plugin is installed and Yoast SEO is active on the client site."
					/>
				</SectionPanel>
			) : (
				<>
					<SectionPanel title="Schema Types in Use" caption="Site snapshot · not period-scoped">
						<SchemaTypesChips summary={seoAgg.schema_types_summary} pagesWithoutSchema={seoAgg.pages_without_schema} />
					</SectionPanel>
					<SectionPanel
						title={`All Pages — SEO Health (${filteredPages.length})`}
						caption="Site snapshot · not period-scoped"
						action={seoAgg.cornerstone_count > 0 ? (
							<span className="inline-flex items-center gap-1 rounded-full bg-amber-light px-2.5 py-1 text-xs font-medium text-amber-dark">
								<Star className="h-3 w-3" /> {seoAgg.cornerstone_count} Cornerstone
							</span>
						) : undefined}
					>
						{filteredPages.length === 0 ? (
							<IntegrationStatePanel state="empty" message="No pages match this filter" />
						) : (
							<SeoHealthTable pages={filteredPages} />
						)}
					</SectionPanel>
				</>
			)}
		</div>
	);
}
